import {Trans} from '@lingui/macro';
import React from 'react';
import styled from 'styled-components';

import {Link} from '../../../../../../../../src/components/common/links/link';
import {accountsCustomFieldsLearnMoreLink} from '../../../../../../../../src/helpers/accountHelpers';
import {downloadFile} from '../../../../../../../../src/helpers/fileHelpers';
import {
  ContactsManagerAccountsCsvErrorsEnum,
  ContactsManagerContactsCsvErrorsEnum,
  ContactsManagerCsvErrorsEnum,
  ContactsManagerFileError,
} from '../../../../../../../../src/store/panels/contacts/contactsManager/file/contactsManagerFileState';
import {fontWeights} from '../../../../../../../shared/ui/src/styles/fontStyles';

/*
 * Constants.
 */

export const accountsCSVTemplateUrl =
  'https://drive.google.com/file/d/1sXK-m9SVpixCmruBCo5_K-HM1qqsGtJg/view?usp=sharing';
export const contactsCSVTemplateUrl =
  'https://www.dropbox.com/s/584m9zit84wp1f7/Front%20Contact%20Import%20Template.csv?dl=0';
export const contactsManagerImportDialogHeight = 572; // px.

/*
 * Styles.
 */

const StyledDownloadFileSpan = styled.span`
  text-decoration: underline;
  font-weight: ${fontWeights.semibold};
`;

/*
 * Helpers.
 */

export function renderAccountsCSVError(error: ContactsManagerFileError, file: File, downloadCSVLink: string) {
  switch (error.error) {
    case ContactsManagerCsvErrorsEnum.NO_HEADER:
      return (
        <>
          <Trans id="accounts-manager-import-accounts-dialog-error-no-header">
            No header row was found. A header row with at least a “name” column is required.
          </Trans>{' '}
          {renderDownloadCSVLink(downloadCSVLink)}
        </>
      );
    case ContactsManagerCsvErrorsEnum.MISSING_HEADER_COLUMN:
      return (
        <>
          <Trans id="accounts-manager-import-accounts-dialog-error-missing-header-column">
            A “name” column was not found. Download the CSV template.
          </Trans>{' '}
          {renderDownloadCSVLink(downloadCSVLink)}
        </>
      );
    case ContactsManagerCsvErrorsEnum.UNRECOGNIZED_HEADERS:
      return (
        <>
          <Trans id="accounts-manager-import-accounts-dialog-error-unrecognized-header">
            An unrecognized column was found. Please remove or rename the column, or create a matching
          </Trans>{' '}
          <Link to={accountsCustomFieldsLearnMoreLink}>
            <Trans id="accounts-manager-import-accounts-dialog-account-custom-field-link">
              account custom field.
            </Trans>
          </Link>
        </>
      );
    // TODO: Remove this when we pass it as MISSING_NAME instead of MISSING_FIELDS
    case ContactsManagerAccountsCsvErrorsEnum.MISSING_FIELDS:
    case ContactsManagerAccountsCsvErrorsEnum.MISSING_NAME:
      return (
        <Trans id="accounts-manager-import-accounts-dialog-missing-fields">
          Error on row {error.row}: Name must be included.
        </Trans>
      );
    case ContactsManagerCsvErrorsEnum.TOO_MANY_ROWS:
      return (
        <Trans id="accounts-manager-import-accounts-dialog-error-too-many-rows">
          The uploaded file exceeds the maximum size of 3000 rows.
        </Trans>
      );
    case ContactsManagerAccountsCsvErrorsEnum.ROWS_ERRORS_WITH_DOWNLOAD:
      return renderDownloadUpdatedCSV(file);
    default:
      return renderGenericError(error, downloadCSVLink);
  }
}

export function renderContactsCSVError(error: ContactsManagerFileError, file: File, downloadCSVLink: string) {
  switch (error.error) {
    case ContactsManagerContactsCsvErrorsEnum.CSV_ROW_ERROR:
      return renderDownloadUpdatedCSV(file);
    case ContactsManagerCsvErrorsEnum.TOO_MANY_ROWS:
      return (
        <Trans id="contacts-manager-import-contacts-dialog-error-too-many-rows">
          The uploaded file exceeds the maximum size of 3000 rows.
        </Trans>
      );
    default:
      return renderGenericError(error, downloadCSVLink);
  }
}

function renderGenericError(error: ContactsManagerFileError, downloadCSVLink: string) {
  switch (error.error) {
    case ContactsManagerCsvErrorsEnum.NO_HEADER:
      return (
        <>
          <Trans id="contacts-manager-import-dialog-error-no-header">No header row was found.</Trans>{' '}
          {renderDownloadCSVLink(downloadCSVLink)}
        </>
      );
    case ContactsManagerCsvErrorsEnum.EMPTY_HEADERS:
      return (
        <>
          <Trans id="contacts-manager-import-dialog-error-empty-header">A column heading is empty.</Trans>{' '}
          {renderDownloadCSVLink(downloadCSVLink)}
        </>
      );
    case ContactsManagerCsvErrorsEnum.PARSE_ERROR_MORE_ROWS_THAN_HEADER:
      return (
        <Trans id="contacts-manager-import-dialog-parse-error-more-rows-than-header">
          A row was found with more rows than specified in the header columns.
        </Trans>
      );
    case ContactsManagerCsvErrorsEnum.INVALID_DUPLICATE_HEADERS:
      return (
        <Trans id="contacts-manager-import-dialog-error-invalid-duplicate-headers">
          Duplicate columns were found. Please remove the duplicate columns.
        </Trans>
      );
    case ContactsManagerCsvErrorsEnum.UNRECOGNIZED_HEADERS:
      return (
        <Trans id="contacts-manager-import-dialog-error-unrecognized-header">
          An unrecognized column was found. Please remove or rename the column.
        </Trans>
      );
    case ContactsManagerCsvErrorsEnum.TOO_MANY_ROWS:
      return (
        <Trans id="contacts-manager-import-dialog-error-too-many-rows">
          The uploaded file exceeds the maximum rows.
        </Trans>
      );
    case ContactsManagerCsvErrorsEnum.INVALID_FILE_TYPE:
      return (
        <Trans id="contacts-manager-import-dialog-invalid-file-type">
          The uploaded file is not a .csv file.
        </Trans>
      );
    default:
      return <Trans id="contacts-manager-import-dialog-error-unknown">An unknown error has occurred.</Trans>;
  }
}

function renderDownloadCSVLink(link: string) {
  return (
    <Link to={link}>
      <Trans id="contacts-manager-import-dialog-download-csv-template">Download the CSV template</Trans>
    </Link>
  );
}

function renderDownloadUpdatedCSV(file: File) {
  return (
    <>
      <Trans id="contacts-manager-import-dialog-errors-found">Errors found.</Trans>{' '}
      <StyledDownloadFileSpan
        onClick={() => {
          downloadFile(document, file);
        }}
      >
        <Trans id="contacts-manager-import-dialog-errors-found-download-updated-csv">
          Download updated CSV
        </Trans>
      </StyledDownloadFileSpan>{' '}
      <Trans id="contacts-manager-import-dialog-errors-found-to-view">to view errors.</Trans>
    </>
  );
}
